/**
 * DNA Funnel AI - Sistema de Captação de Leads
 * Fase 5 do DNA Growth Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, TrendingUp, Target, Phone, Mail, MessageCircle,
  Plus, Search, Filter, Sparkles, ArrowRight, Star, Eye,
  Instagram, Facebook, Globe, UserPlus, Clock, CheckCircle,
  XCircle, Loader2, ChevronRight, BarChart3
} from 'lucide-react';

const API_URL = window.location.origin || '';

const STATUS_CONFIG = {
  novo: { label: 'Novo', color: 'bg-blue-500', icon: UserPlus },
  contatado: { label: 'Contatado', color: 'bg-yellow-500', icon: Phone },
  agendado: { label: 'Agendado', color: 'bg-purple-500', icon: Clock },
  visitou: { label: 'Visitou', color: 'bg-indigo-500', icon: Eye },
  matriculado: { label: 'Matriculado', color: 'bg-green-500', icon: CheckCircle },
  perdido: { label: 'Perdido', color: 'bg-red-500', icon: XCircle }
};

const ORIGEM_CONFIG = {
  instagram: { label: 'Instagram', icon: Instagram, color: 'text-pink-500' },
  facebook: { label: 'Facebook', icon: Facebook, color: 'text-blue-600' },
  site: { label: 'Site', icon: Globe, color: 'text-green-500' },
  indicacao: { label: 'Indicação', icon: Users, color: 'text-purple-500' },
  outro: { label: 'Outro', icon: UserPlus, color: 'text-gray-500' }
};

export default function DnaFunnelAI() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsQuentes, setLeadsQuentes] = useState([]);
  
  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [filtroOrigem, setFiltroOrigem] = useState('all');
  const [filtroBusca, setFiltroBusca] = useState('');
  
  // Modal Lead
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sugestaoIA, setSugestaoIA] = useState(null);
  const [loadingSugestao, setLoadingSugestao] = useState(false);
  
  // Novo Lead
  const [showNovoLead, setShowNovoLead] = useState(false);
  const [novoLead, setNovoLead] = useState({
    nome: '', email: '', telefone: '', origem: 'site', interesse: '', mensagem: ''
  });
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };
  
  const carregarDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/funnel/dashboard`, { headers: getAuthHeaders() });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  }, []);
  
  const carregarLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroStatus !== 'all') params.append('status', filtroStatus);
      if (filtroOrigem !== 'all') params.append('origem', filtroOrigem);
      
      const res = await fetch(`${API_URL}/api/growth/funnel/leads?${params.toString()}`, { headers: getAuthHeaders() });
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, filtroOrigem]);
  
  const carregarLeadsQuentes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/funnel/leads/quentes`, { headers: getAuthHeaders() });
      const data = await res.json();
      setLeadsQuentes(data.leads_quentes || []);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarDashboard();
    carregarLeadsQuentes();
  }, [carregarDashboard, carregarLeadsQuentes]);
  
  useEffect(() => {
    if (activeTab === 'leads') {
      carregarLeads();
    }
  }, [activeTab, carregarLeads]);
  
  const criarLead = async () => {
    if (!novoLead.nome || !novoLead.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/growth/funnel/leads`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(novoLead)
      });
      
      if (res.ok) {
        toast.success('Lead cadastrado com sucesso!');
        setShowNovoLead(false);
        setNovoLead({ nome: '', email: '', telefone: '', origem: 'site', interesse: '', mensagem: '' });
        carregarLeads();
        carregarDashboard();
      } else {
        toast.error('Erro ao cadastrar lead');
      }
    } catch (error) {
      toast.error('Erro ao cadastrar lead');
    }
  };
  
  const atualizarStatusLead = async (leadId, novoStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/growth/funnel/leads/${leadId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: novoStatus })
      });
      
      if (res.ok) {
        toast.success('Status atualizado!');
        carregarLeads();
        carregarDashboard();
        setShowLeadModal(false);
      }
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };
  
  const gerarSugestaoIA = async (leadId) => {
    setLoadingSugestao(true);
    try {
      const res = await fetch(`${API_URL}/api/growth/funnel/leads/${leadId}/sugestao-ia`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setSugestaoIA(data.sugestao);
    } catch (error) {
      toast.error('Erro ao gerar sugestão');
    } finally {
      setLoadingSugestao(false);
    }
  };
  
  const leadsFiltrados = leads.filter(lead => {
    if (filtroBusca) {
      const busca = filtroBusca.toLowerCase();
      return lead.nome?.toLowerCase().includes(busca) || 
             lead.email?.toLowerCase().includes(busca) ||
             lead.telefone?.includes(busca);
    }
    return true;
  });
  
  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Target className="w-8 h-8 text-orange-500" />
            DNA Funnel AI
          </h1>
          <p className="text-gray-500 mt-1">Sistema de Captação de Leads</p>
        </div>
        <Button onClick={() => setShowNovoLead(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" />
          Novo Lead
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="funil">Funil</TabsTrigger>
        </TabsList>
        
        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Métricas */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Total Leads</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.total_leads || 0}</p>
                  </div>
                  <Users className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Leads/Mês</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.leads_mes || 0}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Conversões</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.conversoes_mes || 0}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm">Taxa Conversão</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.taxa_conversao || 0}%</p>
                  </div>
                  <Target className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm">Leads Quentes</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.leads_quentes || 0}</p>
                  </div>
                  <Star className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Leads Quentes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Leads Quentes - Prioridade de Contato
              </CardTitle>
              <CardDescription>Leads com maior probabilidade de conversão</CardDescription>
            </CardHeader>
            <CardContent>
              {leadsQuentes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum lead quente no momento</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {leadsQuentes.slice(0, 5).map(lead => {
                    const origemConfig = ORIGEM_CONFIG[lead.origem] || ORIGEM_CONFIG.outro;
                    const OrigemIcon = origemConfig.icon;
                    
                    return (
                      <div 
                        key={lead.id}
                        className="flex items-center justify-between p-4 bg-white border rounded-xl hover:shadow-md transition-all cursor-pointer"
                        onClick={() => { setSelectedLead(lead); setShowLeadModal(true); }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                            {lead.score}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{lead.nome}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <OrigemIcon className={`w-4 h-4 ${origemConfig.color}`} />
                              <span>{origemConfig.label}</span>
                              {lead.interesse && <span>• {lead.interesse}</span>}
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">
                          Contatar <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Origem */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Leads por Origem</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(dashboard?.por_origem || {}).map(([origem, count]) => {
                    const config = ORIGEM_CONFIG[origem] || ORIGEM_CONFIG.outro;
                    const Icon = config.icon;
                    const total = dashboard?.resumo?.total_leads || 1;
                    const percent = Math.round(count / total * 100);
                    
                    return (
                      <div key={origem} className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${config.color}`} />
                        <span className="flex-1 text-gray-600">{config.label}</span>
                        <span className="font-medium">{count}</span>
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div className={`h-2 rounded-full bg-orange-500`} style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Funil de Conversão</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(dashboard?.funil || {}).map(([status, count]) => {
                    const config = STATUS_CONFIG[status];
                    if (!config) return null;
                    
                    return (
                      <div key={status} className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${config.color}`} />
                        <span className="flex-1 text-gray-600">{config.label}</span>
                        <span className="font-bold text-gray-800">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* LEADS */}
        <TabsContent value="leads" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-48">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input 
                      placeholder="Buscar lead..."
                      value={filtroBusca}
                      onChange={(e) => setFiltroBusca(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos Status</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filtroOrigem} onValueChange={setFiltroOrigem}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Origem" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas Origens</SelectItem>
                    {Object.entries(ORIGEM_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Lista */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : leadsFiltrados.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhum lead encontrado</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {leadsFiltrados.map(lead => {
                const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.novo;
                const origemConfig = ORIGEM_CONFIG[lead.origem] || ORIGEM_CONFIG.outro;
                const OrigemIcon = origemConfig.icon;
                
                return (
                  <Card 
                    key={lead.id}
                    className="hover:shadow-md transition-all cursor-pointer"
                    onClick={() => { setSelectedLead(lead); setSugestaoIA(null); setShowLeadModal(true); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-gray-600">{lead.score}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{lead.nome}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {lead.email}
                              </span>
                              {lead.telefone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {lead.telefone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <OrigemIcon className={`w-5 h-5 ${origemConfig.color}`} />
                          <Badge className={`${statusConfig.color} text-white`}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        {/* FUNIL VISUAL */}
        <TabsContent value="funil">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Funil de Conversão Visual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-2 py-8">
                {Object.entries(dashboard?.funil || {}).map(([status, count], idx) => {
                  const config = STATUS_CONFIG[status];
                  if (!config) return null;
                  const total = dashboard?.resumo?.total_leads || 1;
                  const width = Math.max(20, 100 - idx * 15);
                  
                  return (
                    <div 
                      key={status}
                      className={`${config.color} text-white py-4 px-6 rounded-lg flex items-center justify-between transition-all hover:scale-105`}
                      style={{ width: `${width}%` }}
                    >
                      <span className="font-medium">{config.label}</span>
                      <span className="text-2xl font-bold">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal Novo Lead */}
      <Dialog open={showNovoLead} onOpenChange={setShowNovoLead}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Lead</DialogTitle>
            <DialogDescription>Cadastre um novo lead manualmente</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input 
              placeholder="Nome completo *"
              value={novoLead.nome}
              onChange={(e) => setNovoLead({...novoLead, nome: e.target.value})}
            />
            <Input 
              type="email"
              placeholder="Email *"
              value={novoLead.email}
              onChange={(e) => setNovoLead({...novoLead, email: e.target.value})}
            />
            <Input 
              placeholder="Telefone"
              value={novoLead.telefone}
              onChange={(e) => setNovoLead({...novoLead, telefone: e.target.value})}
            />
            <Select value={novoLead.origem} onValueChange={(v) => setNovoLead({...novoLead, origem: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ORIGEM_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input 
              placeholder="Série de interesse"
              value={novoLead.interesse}
              onChange={(e) => setNovoLead({...novoLead, interesse: e.target.value})}
            />
            <Textarea 
              placeholder="Mensagem/Observações"
              value={novoLead.mensagem}
              onChange={(e) => setNovoLead({...novoLead, mensagem: e.target.value})}
            />
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowNovoLead(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={criarLead} className="flex-1 bg-orange-500 hover:bg-orange-600">
                Cadastrar Lead
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal Detalhes Lead */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="max-w-lg">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-orange-600">{selectedLead.score}</span>
                  </div>
                  {selectedLead.nome}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Telefone</p>
                    <p className="font-medium">{selectedLead.telefone || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Origem</p>
                    <p className="font-medium">{ORIGEM_CONFIG[selectedLead.origem]?.label}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Interesse</p>
                    <p className="font-medium">{selectedLead.interesse || 'Não especificado'}</p>
                  </div>
                </div>
                
                {selectedLead.mensagem && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-sm mb-1">Mensagem</p>
                    <p className="text-gray-700">{selectedLead.mensagem}</p>
                  </div>
                )}
                
                {/* Atualizar Status */}
                <div>
                  <p className="text-gray-500 text-sm mb-2">Atualizar Status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <Button
                        key={key}
                        size="sm"
                        variant={selectedLead.status === key ? 'default' : 'outline'}
                        className={selectedLead.status === key ? config.color : ''}
                        onClick={() => atualizarStatusLead(selectedLead.id, key)}
                      >
                        {config.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Sugestão IA */}
                <div className="border-t pt-4">
                  <Button
                    onClick={() => gerarSugestaoIA(selectedLead.id)}
                    disabled={loadingSugestao}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    {loadingSugestao ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Gerar Sugestão de Abordagem (IA)
                  </Button>
                  
                  {sugestaoIA && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{sugestaoIA}</p>
                    </div>
                  )}
                </div>
                
                {/* Ações rápidas */}
                <div className="flex gap-2 pt-2">
                  {selectedLead.telefone && (
                    <Button variant="outline" size="sm" className="flex-1" asChild>
                      <a href={`https://wa.me/55${selectedLead.telefone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
                        WhatsApp
                      </a>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="flex-1" asChild>
                    <a href={`mailto:${selectedLead.email}`}>
                      <Mail className="w-4 h-4 mr-2 text-blue-500" />
                      Email
                    </a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
